/**
 * EndOfLessonCard.jsx · the card shown over the player when a lesson ends (all six players).
 *
 * Layout (the approved mockups, "End of Lesson Card" canvas):
 *   · the lesson just finished sits on the dark space, full width along the top: a gold check,
 *     "Lesson Complete", its title, and Watch Again on the right
 *   · what's next is the one card below, with its main button full width:
 *       next lesson  → Play Next Lesson      locked        → Unlock with <Tier>
 *       course done  → Back to Course        not in course → Open Navigator
 * Nothing moves on by itself: the next lesson starts only from a tap.
 * The card measures its own space and switches to the compact (phone) layout in a small player.
 *
 * endCardInfo() gathers the details a page passes in as `info` (lesson title, course, next lesson's
 * course · chapter line, the course page's path). Pages add the two actions: onOpenNavigator and
 * onBackToCourse.
 */
import React, { useEffect, useRef, useState } from 'react';
import { TIERS } from './config';

const GOLD_FALLBACK = '#C9A962';
const TEXT = '#F5F5F0';
const MUTED = '#A0A090';

export function endCardInfo({ catalog, ctxCourse, curIdx, upNext, video }) {
  const inCourse = !!ctxCourse && curIdx >= 0;
  const subject = ctxCourse ? (catalog || []).find((su) => (su.courses || []).some((c) => c.id === ctxCourse.id)) : null;
  const upNextChapter = ctxCourse && upNext
    ? (ctxCourse.chapters || []).find((ch) => (ch.sections || []).some((sec) => (sec.topics || []).includes(upNext))) || null
    : null;
  return {
    lessonTitle: (video && video.title) || '',
    inCourse,
    courseName: ctxCourse ? ctxCourse.name : '',
    upNextMeta: [ctxCourse && ctxCourse.name, upNextChapter && upNextChapter.name].filter(Boolean).join(' · '),
    coursePath: subject && subject.slug && ctxCourse && ctxCourse.slug ? `/courses/${subject.slug}/${ctxCourse.slug}` : '/courses',
  };
}

// Wide desktop pages show the navigator as a column beside the video: bring it into view instead of opening a drawer.
export function focusNavigatorColumn() {
  if (typeof document === 'undefined') return false;
  const col = document.querySelector('aside[aria-label="Course Navigator"]');
  if (!col) return false;
  try { col.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { col.scrollIntoView(); }
  const first = col.querySelector('button');
  if (first) { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } }
  return true;
}

const Icon = {
  check: (s, c = 'currentColor') => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>,
  replay: (s, c) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>,
  play: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>,
  lock: (s, c = 'currentColor') => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>,
  list: (s, c = 'currentColor') => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h12" /></svg>,
};

const rgba = (hex, a) => {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return `rgba(201,169,98,${a})`;
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${a})`;
};

export default function EndOfLessonCard({ colors, upNext, upNextLocked, info, onUpNext, onUnlock, onReplay }) {
  const gold = (colors && colors.accent) || GOLD_FALLBACK;
  const i = info || {};
  const rootRef = useRef(null);
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const pick = () => { const r = el.getBoundingClientRect(); setCompact(r.width < 700 || r.height < 400); };
    pick();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(pick); ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const state = upNext && !upNextLocked ? 'next' : upNext && upNextLocked ? 'locked' : i.inCourse ? 'complete' : 'nocourse';
  const tierName = upNext && TIERS[upNext.tierRequired] ? TIERS[upNext.tierRequired].name : 'Pro';
  const c = compact;
  const stop = (fn) => (e) => { e.stopPropagation(); if (fn) fn(); };

  // ── the finished lesson: native to the space ──
  const done = (
    <section aria-label="Lesson you finished" style={{ display: 'flex', alignItems: 'center', gap: c ? '10px' : '14px', padding: c ? '2px 4px 8px' : '0 0 22px', borderBottom: '1px solid rgba(245,245,240,0.08)' }}>
      <span style={{ width: c ? 26 : 36, height: c ? 26 : 36, borderRadius: '50%', background: gold, color: '#141414', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 0 ${c ? 14 : 22}px ${rgba(gold, 0.45)}` }}>{Icon.check(c ? 14 : 18)}</span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: c ? '1px' : '3px', minWidth: 0, flexGrow: 1 }}>
        <span style={{ fontSize: c ? '10px' : '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: gold }}>Lesson Complete</span>
        <span style={{ fontFamily: '"Cormorant Garamond",Georgia,serif', fontSize: c ? '17px' : '26px', fontWeight: 600, lineHeight: 1.1, color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.lessonTitle}</span>
      </span>
      <button type="button" onClick={stop(onReplay)} style={{ height: '44px', padding: c ? '0 12px' : '0 18px', flexShrink: 0, borderRadius: '12px', border: `1px solid ${rgba(gold, 0.4)}`, background: rgba(gold, 0.1), color: TEXT, fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif' }}>
        {Icon.replay(15, gold)}Watch Again
      </button>
    </section>
  );

  // ── what's next: the one card ──
  const thumbW = c ? 72 : 176, thumbH = c ? 40 : 99;
  const thumb = (locked) => (
    <span style={{ width: thumbW, height: thumbH, borderRadius: c ? '6px' : '10px', flexShrink: 0, position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #3A3122 0%, #1F1C17 55%, #141414 100%)', border: '1px solid rgba(245,245,240,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {upNext && upNext.thumbnailUrl && <img src={upNext.thumbnailUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: locked ? 0.35 : 1 }} />}
      {locked && <span style={{ position: 'relative' }}>{Icon.lock(c ? 18 : 20, gold)}</span>}
      {!locked && upNext && upNext.duration && <span style={{ position: 'absolute', right: c ? '4px' : '8px', bottom: c ? '4px' : '8px', padding: '1px 5px', borderRadius: '4px', background: 'rgba(10,10,10,0.75)', fontSize: c ? '10px' : '11px', fontWeight: 600, color: TEXT }}>{upNext.duration}</span>}
    </span>
  );
  const tile = (icon) => (
    <span style={{ width: c ? 40 : 72, height: c ? 40 : 72, borderRadius: c ? '10px' : '14px', flexShrink: 0, background: rgba(gold, 0.1), border: `1px solid ${rgba(gold, 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
  );
  const text = (label, title, meta) => (
    <span style={{ display: 'flex', flexDirection: 'column', gap: c ? '3px' : '6px', minWidth: 0 }}>
      <span style={{ fontSize: c ? '10px' : '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: gold }}>{label}</span>
      <span style={{ fontFamily: '"Cormorant Garamond",Georgia,serif', fontSize: c ? '16px' : '27px', fontWeight: 600, lineHeight: 1.12, color: TEXT, ...(c ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}) }}>{title}</span>
      {!c && meta && <span style={{ fontSize: '13px', lineHeight: 1.35, color: MUTED }}>{meta}</span>}
    </span>
  );
  const primary = (icon, label, onClick) => (
    <button type="button" onClick={stop(onClick)} style={{ width: '100%', height: c ? '44px' : '48px', borderRadius: '12px', border: 'none', background: gold, color: '#141414', fontSize: c ? '14px' : '15px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px', cursor: 'pointer', boxShadow: `0 0 20px ${rgba(gold, 0.35)}`, fontFamily: '"DM Sans",sans-serif' }}>{icon}{label}</button>
  );
  const row = (children) => <div style={{ display: 'flex', alignItems: 'center', gap: c ? '12px' : '18px' }}>{children}</div>;

  let block = null, action = null;
  const upName = upNext ? (upNext.name || upNext.title) : '';
  if (state === 'next') {
    block = (
      <button type="button" aria-label={`Play Next Lesson: ${upName}`} onClick={stop(onUpNext)} style={{ display: 'flex', alignItems: 'center', gap: c ? '12px' : '18px', padding: 0, border: 'none', background: 'none', color: 'inherit', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
        {thumb(false)}{text('Up Next', upName, i.upNextMeta)}
      </button>
    );
    action = primary(Icon.play(15), 'Play Next Lesson', onUpNext);
  } else if (state === 'locked') {
    block = row(<>{thumb(true)}{text('Next Lesson', upName, `Included with ${tierName}`)}</>);
    action = primary(Icon.lock(15), `Unlock with ${tierName}`, onUnlock);
  } else if (state === 'complete') {
    block = row(<>{tile(Icon.check(c ? 20 : 30, gold))}{text('Course Complete', i.courseName ? `You Finished ${i.courseName}` : 'You Finished the Course', i.lessonTitle ? `${i.lessonTitle} was the final lesson in this course.` : '')}</>);
    action = primary(Icon.list(15), 'Back to Course', i.onBackToCourse);
  } else {
    block = row(<>{tile(Icon.list(c ? 20 : 30, gold))}{text('Keep Going', 'Choose a Course or Playlist', "This video isn't part of a course yet. Pick a course or playlist in the navigator and the next video will be ready here.")}</>);
    action = primary(Icon.list(15), 'Open Navigator', i.onOpenNavigator);
  }

  const card = (
    <section aria-label="What's next" style={{ width: c ? 'min(292px, 100%)' : '580px', maxWidth: '100%', boxSizing: 'border-box', padding: c ? '8px 9px 9px' : '24px 28px 28px', background: '#161616', border: `1px solid ${rgba(gold, 0.45)}`, borderRadius: c ? '12px' : '16px', boxShadow: c ? `0 0 18px ${rgba(gold, 0.12)}` : `0 24px 64px rgba(0,0,0,0.6), 0 0 32px ${rgba(gold, 0.1)}`, display: 'flex', flexDirection: 'column', gap: c ? '8px' : '18px' }}>
      {block}
      {action}
    </section>
  );

  return (
    <div ref={rootRef} role="dialog" aria-label="End of lesson" data-end-card={state} onClick={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
      style={{ position: 'absolute', inset: 0, zIndex: 30, overflowY: 'auto', boxSizing: 'border-box', padding: c ? '8px' : '32px 40px 40px', display: 'flex', flexDirection: 'column', gap: c ? '10px' : 0,
        background: `radial-gradient(ellipse at 50% 58%, ${rgba(gold, 0.12)} 0%, ${rgba(gold, 0)} 60%), rgba(10,10,10,0.97)`, backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', color: TEXT, fontFamily: '"DM Sans",sans-serif', textAlign: 'left' }}>
      {done}
      <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: c ? 0 : '22px' }}>{card}</div>
    </div>
  );
}
